import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import {
  Phone,
  MessageCircle,
  MessageSquare,
  Flame,
  Home,
  Wrench,
  Zap,
  Thermometer,
  Droplet,
  MapPin,
  Clock,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { businessInfo, pageHeroImages } from "../data/mockData";
import { trackContactClick } from "../lib/analytics";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const services = [
  { Icon: Flame, label: "Boiler Servicing" },
  { Icon: Home, label: "Landlord Gas Safety Certificate (CP12)" },
  { Icon: Wrench, label: "Boiler Repair" },
  { Icon: Zap, label: "New Boiler Quote" },
  { Icon: Thermometer, label: "Heating Issue" },
  { Icon: Droplet, label: "Plumbing Job" },
];

// Builds a WhatsApp link with a pre-filled message
const waLink = (text) =>
  `${businessInfo.whatsapp}?text=${encodeURIComponent(text)}`;

const ContactPage = () => {
  return (
    <>
      <Seo {...PAGE_SEO.contact} />
      <PageHero
        eyebrow="Contact"
        image={pageHeroImages.contact}
        title="Contact Lee — Gas Engineer Cardiff, Pontypridd, Bridgend &amp; Caerphilly"
        subtitle="The fastest way to reach Lee is on WhatsApp — send a quick message and we'll come straight back to you. Based in Porth, covering Cardiff, Pontypridd, Bridgend, Caerphilly and Rhondda Cynon Taf. Trusted by homeowners, landlords, HMO owners and property investors."
      />

      {/* 3 contact cards */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase text-brand-blue mb-3">
              Three Easy Ways to Reach Lee
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-brand-navy tracking-tight">
              Pick whichever suits you best
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* WhatsApp — primary */}
            <a
              href={businessInfo.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group relative bg-brand-whatsapp text-white rounded-2xl p-7 hover:bg-brand-whatsapp-dark transition-all shadow-lg hover:shadow-xl md:scale-105 md:-mt-2"
            >
              <span className="absolute top-4 right-4 text-[10px] font-black tracking-widest uppercase bg-white/20 backdrop-blur rounded-full px-2.5 py-1">
                Fastest
              </span>
              <div className="h-14 w-14 rounded-xl bg-white/15 flex items-center justify-center mb-4">
                <MessageCircle className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-black mb-1">WhatsApp</h3>
              <p className="text-base font-bold mb-3">Tap to message</p>
              <p className="text-sm opacity-90 mb-4">
                Send photos, fault codes, postcode and job details. Most messages
                are answered within the hour.
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-2.5 transition-all">
                Open WhatsApp <ArrowRight className="h-4 w-4" />
              </span>
            </a>

            {/* Call */}
            <a
              href={businessInfo.phoneTel}
              onClick={() => trackContactClick("call", "contact_card")}
              data-testid="contact-call-card"
              className="group bg-white border border-slate-200 rounded-2xl p-7 hover:border-brand-navy hover:shadow-lg transition-all"
            >
              <div className="h-14 w-14 rounded-xl bg-brand-navy text-white flex items-center justify-center mb-4 group-hover:bg-brand-navy-light">
                <Phone className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-brand-navy mb-1">Call</h3>
              <p className="text-2xl font-black text-brand-navy mb-3">
                {businessInfo.phone}
              </p>
              <p className="text-sm text-slate-600">
                Speak directly with Lee about your enquiry.
              </p>
            </a>

            {/* Text */}
            <a
              href={businessInfo.sms}
              onClick={() => trackContactClick("text", "contact_card")}
              data-testid="contact-text-card"
              className="group bg-white border border-slate-200 rounded-2xl p-7 hover:border-brand-blue hover:shadow-lg transition-all"
            >
              <div className="h-14 w-14 rounded-xl bg-brand-blue text-white flex items-center justify-center mb-4 group-hover:bg-brand-blue-light">
                <MessageSquare className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-brand-navy mb-1">Text Message</h3>
              <p className="text-base font-bold text-brand-blue mb-3">Tap to text</p>
              <p className="text-sm text-slate-600">
                Prefer to text? Send a message and we'll get back to you.
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp by-service selector */}
      <section className="bg-brand-slate-light py-16 lg:py-20" data-testid="whatsapp-selector">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Left — explanation */}
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 bg-brand-whatsapp/15 border border-brand-whatsapp/30 rounded-full px-3 py-1.5 mb-5">
                <MessageCircle className="h-4 w-4 text-brand-whatsapp-dark" />
                <span className="text-[11px] font-black tracking-widest uppercase text-brand-whatsapp-dark">
                  WhatsApp Enquiry
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-brand-navy mb-4 tracking-tight">
                Quick WhatsApp Enquiry
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5">
                Tap the service you need and we'll open WhatsApp with a
                pre-filled message — just add your postcode and any details, then
                send.
              </p>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-blue shrink-0 mt-0.5" />
                  Gas Safe registered engineer
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-blue shrink-0 mt-0.5" />
                  Most messages answered within the hour
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-blue shrink-0 mt-0.5" />
                  Free quotes — no pressure, no hard selling
                </li>
              </ul>
            </div>

            {/* Right — service buttons */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {services.map(({ Icon, label }) => (
                  <a
                    key={label}
                    href={waLink(
                      `Hi Lee, I'd like to enquire about: ${label}.\nMy postcode is: \nDetails: `
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackContactClick("whatsapp", "contact_service", { service: label })}
                    data-testid={`wa-service-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-200 hover:border-brand-whatsapp hover:shadow-md transition-all"
                  >
                    <div className="h-11 w-11 rounded-lg bg-brand-blue-soft text-brand-blue flex items-center justify-center shrink-0 group-hover:bg-brand-whatsapp group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-brand-navy leading-tight">
                        {label}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <MessageCircle className="h-3 w-3 text-brand-whatsapp" />
                        Open WhatsApp
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-brand-whatsapp group-hover:translate-x-1 transition-all shrink-0" />
                  </a>
                ))}
              </div>

              <a
                href={businessInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContactClick("whatsapp", "contact_service", { service: "general" })}
                data-testid="wa-general"
                className="mt-4 inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-brand-whatsapp hover:bg-brand-whatsapp-dark text-white font-bold rounded-xl px-6 py-3.5 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                Or just message us directly
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Side info */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-brand-blue" />
                <h3 className="text-lg font-bold text-brand-navy">Business Hours</h3>
              </div>
              <p className="text-base font-semibold text-brand-navy">
                {businessInfo.hoursSummary}
              </p>
              <p className="text-sm text-slate-600 mt-2">
                {businessInfo.emergencyNote}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-5 w-5 text-brand-blue" />
                <h3 className="text-lg font-bold text-brand-navy">Areas Covered</h3>
              </div>
              <p className="text-sm text-slate-700 mb-3">
                Based in Porth, we regularly work throughout:
              </p>
              <ul className="space-y-1.5 text-sm">
                {businessInfo.serviceAreas.map((a) => (
                  <li key={a} className="flex items-center gap-2 text-brand-navy font-semibold">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
                    {a}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-3">and surrounding areas.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-brand-navy mb-3">
                Services We Can Help With
              </h3>
              <ul className="space-y-2 text-sm">
                {services.map(({ Icon, label }) => (
                  <li key={label} className="flex items-center gap-2 text-slate-700">
                    <Icon className="h-4 w-4 text-brand-blue shrink-0" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA
        title="Looking for a reliable Gas Safe engineer?"
        lines={["Tap WhatsApp, call or text — whichever's easiest."]}
      />
    </>
  );
};

export default ContactPage;
